/**
 * participationAudioProfiles.js ✦ iter205
 * ─────────────────────────────────────────────────────────────
 * The vocabulary for voice capture. Parallel to (but deliberately
 * SEPARATE from) the camera composition language — audio has its
 * own technical and emotional behaviour patterns, and keeping the
 * two systems apart now prevents architectural contamination later.
 *
 * Per Craig (iter204 → iter205): "audio is emotional infrastructure,
 * not just media capture. Written memory quality depends on visual
 * composition. Voice memory quality depends on intelligibility +
 * warmth + perceived intimacy. Treat audio as its own pipeline
 * layer."
 *
 * Each frozen profile defines:
 *
 *   • sampleRate            — preferred capture sample rate (Hz)
 *   • echoCancellation      — browser AEC flag
 *   • noiseSuppression      — browser NS flag (kept gentle —
 *                              intelligibility-first, not studio-clean)
 *   • autoGainControl       — browser AGC flag
 *   • lowCutHz              — BiquadFilter highpass corner (removes
 *                              rumble + mic handling thuds without
 *                              touching voice fundamentals)
 *   • compressorThresholdDb — DynamicsCompressor knee (clipping guard,
 *                              not loudness maximiser)
 *   • compressorRatio       — gentle ratio (≤3:1) to avoid podcast
 *                              sound; the room is part of the memory
 *   • peakTargetLinear      — output gain target after compression
 *                              (linear 0..1, applied via GainNode)
 *   • clippingGuardLinear   — threshold above which a frame is flagged
 *                              as clipping in telemetry
 *   • silenceTrimRMS        — tail-trim threshold. Head silence is
 *                              PRESERVED (inhale, hesitation, room tone
 *                              are the most human part).
 *   • tailSilenceMaxMs      — clip the trailing silence window down to
 *                              this many ms (small breath out, not
 *                              total dead air)
 *   • presenceFloorRMS      — what "the room feels like when empty".
 *                              Separate from silence detection.
 *                              Future: automatic gain tuning, recap
 *                              cleanup, archive mastering.
 *   • maxDurationSec        — hard cap on recording length
 *   • mimeCandidates        — ordered MediaRecorder negotiation list
 *   • audioBitsPerSecond    — target bitrate
 *
 * Emotional rule (Craig, iter205): "preserve room tone under the
 * voice. Clinking glasses, laughter behind the speaker, distant
 * music, crowd ambience — the room itself is part of the memory.
 * Intelligibility-first, NOT studio-clean."
 */

const baseAudio = {
  sampleRate: 48_000,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  lowCutHz: 80,                 // rumble + handling noise; voice F0 starts ~85
  compressorThresholdDb: -18,
  compressorRatio: 2.5,
  peakTargetLinear: 0.9,
  clippingGuardLinear: 0.96,
  silenceTrimRMS: 0.012,
  tailSilenceMaxMs: 320,
  presenceFloorRMS: 0.005,
  mimeCandidates: [
    // Order matters — ranked by playback compatibility across
    // download paths. Safari falls through to mp4/aac; chrome
    // desktop is happiest with webm/opus.
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ],
  audioBitsPerSecond: 128_000,
};

export const PARTICIPATION_AUDIO_PROFILES = Object.freeze({
  // ── Voice Message — the guestbook recording. Intelligibility-first.
  voice_message: Object.freeze({
    ...baseAudio,
    maxDurationSec: 60,
  }),

  // ── Voice Note — shorter intimate captures. Warmth-first; gentler
  //    compression so the room tone reads more naturally.
  voice_note: Object.freeze({
    ...baseAudio,
    compressorThresholdDb: -22,
    compressorRatio: 2.0,
    peakTargetLinear: 0.85,
    maxDurationSec: 15,
  }),
});

export function getAudioProfile(name) {
  const p = PARTICIPATION_AUDIO_PROFILES[name];
  if (!p) {
    const known = Object.keys(PARTICIPATION_AUDIO_PROFILES).join(', ');
    throw new Error(
      `[useParticipationAudioPipeline] unknown audio profile "${name}". `
      + `Known profiles: ${known}.`,
    );
  }
  return p;
}

export function audioProfileToConstraints(profile, override = {}) {
  return {
    audio: {
      deviceId: override.deviceId ? { exact: override.deviceId } : undefined,
      sampleRate: { ideal: profile.sampleRate },
      echoCancellation: profile.echoCancellation,
      noiseSuppression: profile.noiseSuppression,
      autoGainControl: profile.autoGainControl,
    },
    video: false,
  };
}
